import { useState } from "react";
import { message } from "antd";
import { careService } from "../../services/careService";
import { COMPLIANCE_QUESTIONNAIRES } from "../../pages/care/complianceQuestionnaireRegistry";

const currentUserName = (user = {}) =>
  [user.firstName, user.lastName].filter(Boolean).join(" ") ||
  user.email ||
  user.id ||
  "";

const defaultAnswers = (questions) =>
  Object.fromEntries(
    questions.map((q) => [q.key, q.type === "boolean" ? null : ""]),
  );

const isQuestionVisible = (question, answers) => {
  if (!question.showIf) return true;
  return answers[question.showIf.key] === question.showIf.equals;
};

export default function ComplianceQuestionnaireForm({
  policyCode,
  workspaceId,
  actorId,
  user,
  record,
  readOnly,
  onSaved,
  onCancel,
}) {
  const config = COMPLIANCE_QUESTIONNAIRES[policyCode];
  const [answers, setAnswers] = useState(() =>
    config
      ? { ...defaultAnswers(config.questions), ...(record?.answers || {}) }
      : {},
  );
  const [notes, setNotes] = useState(record?.notes || "");
  const [saving, setSaving] = useState(false);

  if (!config) return null;

  const updateAnswer = (key, value) => {
    setAnswers((current) => {
      const next = { ...current, [key]: value };
      // If a gating question flips away from the value that revealed a
      // follow-up, clear the now-hidden follow-up's answer so a stale value
      // isn't submitted silently.
      config.questions.forEach((q) => {
        if (q.showIf?.key === key && next[key] !== q.showIf.equals) {
          next[q.key] = q.type === "boolean" ? null : "";
        }
      });
      return next;
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (readOnly) return;

    const visibleQuestions = config.questions.filter((q) =>
      isQuestionVisible(q, answers),
    );
    const missing = visibleQuestions.find(
      (q) => q.required && (answers[q.key] === null || answers[q.key] === ""),
    );
    if (missing) {
      message.error(`Please answer: ${missing.prompt}`);
      return;
    }
    setSaving(true);
    try {
      // Only submit answers for currently-visible questions, so a hidden
      // follow-up's stale value never gets persisted.
      const submittedAnswers = Object.fromEntries(
        visibleQuestions.map((q) => [q.key, answers[q.key]]),
      );
      const payload = {
        workspaceId,
        policyCode,
        submittedBy: currentUserName(user),
        answers: submittedAnswers,
        notes: notes.trim() || null,
      };
      const saved = await careService.complianceQuestionnaireResponses.create(
        payload,
        {
          actorId,
          wid: workspaceId,
          workspaceId,
        },
      );
      message.success(`${config.label} questionnaire submitted.`);
      onSaved?.(saved);
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to submit questionnaire.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="assessment-drawer-form compliance-questionnaire-form"
      onSubmit={submit}
    >
      <div className="drawer-actions drawer-actions-top">
        <button
          className="mini-btn pharmacy-drawer-cancel"
          type="button"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          className="mini-btn pharmacy-primary-button"
          type="submit"
          disabled={saving}
        >
          {saving ? "Submitting..." : "Submit"}
        </button>
      </div>

      <section
        className="assessment-drawer-section"
        style={{ paddingInline: 20 }}
      >
        <p className="compliance-questionnaire-reference">{config.reference}</p>
        {config.questions
          .filter((q) => isQuestionVisible(q, answers))
          .map((question) => (
            <div className="compliance-questionnaire-item" key={question.key}>
              <label className="compliance-questionnaire-prompt">
                {question.prompt}
                {question.required ? " *" : ""}
              </label>
              {question.type === "boolean" ? (
                <div className="compliance-questionnaire-yesno">
                  <label className="compliance-questionnaire-checkbox">
                    <input
                      type="checkbox"
                      checked={answers[question.key] === true}
                      onChange={(event) =>
                        updateAnswer(
                          question.key,
                          event.target.checked ? true : null,
                        )
                      }
                      disabled={readOnly}
                    />
                    <span>Yes</span>
                  </label>
                  <label className="compliance-questionnaire-checkbox">
                    <input
                      type="checkbox"
                      checked={answers[question.key] === false}
                      onChange={(event) =>
                        updateAnswer(
                          question.key,
                          event.target.checked ? false : null,
                        )
                      }
                      disabled={readOnly}
                    />
                    <span>No</span>
                  </label>
                </div>
              ) : question.type === "date" ? (
                <input
                  className="compliance-questionnaire-input"
                  type="date"
                  value={answers[question.key] || ""}
                  onChange={(event) =>
                    updateAnswer(question.key, event.target.value)
                  }
                />
              ) : question.type === "text" ? (
                <textarea
                  className="compliance-questionnaire-input compliance-questionnaire-textarea"
                  rows={2}
                  value={answers[question.key] || ""}
                  onChange={(event) =>
                    updateAnswer(question.key, event.target.value)
                  }
                  disabled={readOnly}
                  placeholder="Provide details..."
                />
              ) : (
                <input
                  className="compliance-questionnaire-input"
                  type="number"
                  value={answers[question.key] || ""}
                  onChange={(event) =>
                    updateAnswer(question.key, Number(event.target.value))
                  }
                />
              )}
            </div>
          ))}
      </section>

      <section className="assessment-drawer-section">
        <label className="ncp-form-field">
          <span>Notes</span>
          <textarea
            rows={2}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Optional context or explanation for any 'No' answers."
          />
        </label>
      </section>

      <div className="drawer-actions">
        <button
          className="mini-btn pharmacy-drawer-cancel"
          type="button"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          className="mini-btn pharmacy-primary-button"
          type="submit"
          disabled={saving}
        >
          {saving ? "Submitting..." : "Submit"}
        </button>
      </div>
    </form>
  );
}
